import { useEffect, useMemo, useState } from 'react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function TableOfContents({ markdown, containerRef }: {
  markdown: string;
  containerRef: React.RefObject<HTMLElement>;
}) {
  const items = useMemo<TocItem[]>(() => {
    const lines = markdown.split('\n');
    const out: TocItem[] = [];
    let inCode = false;
    for (const line of lines) {
      if (line.trim().startsWith('```')) { inCode = !inCode; continue; }
      if (inCode) continue;
      const m = /^(#{2,3})\s+(.+?)\s*$/.exec(line);
      if (m) {
        const level = m[1].length;
        const text = m[2].replace(/[*_`]/g, '');
        out.push({ id: slugify(text), text, level });
      }
    }
    return out;
  }, [markdown]);

  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current || items.length === 0) return;
    const headings = items
      .map((i) => containerRef.current!.querySelector<HTMLElement>(`#${CSS.escape(i.id)}`))
      .filter((el): el is HTMLElement => !!el);
    if (headings.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach((h) => obs.observe(h));
    return () => obs.disconnect();
  }, [items, containerRef]);

  if (items.length === 0) return null;

  return (
    <nav aria-label="Table of contents" className="hidden lg:block sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-3">On this page</div>
      <ul className="space-y-1.5 text-sm border-l border-border">
        {items.map((it) => (
          <li key={it.id} style={{ paddingLeft: (it.level - 2) * 12 + 12 }}>
            <a
              href={`#${it.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(it.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                history.replaceState(null, '', `#${it.id}`);
              }}
              className={`block py-0.5 -ml-px border-l-2 pl-3 transition-colors ${
                activeId === it.id
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {it.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
